import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
  StatusBar,
  Switch,
  Platform,
  Linking,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { signOut } from 'firebase/auth';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Typography, Spacing, Radius } from '../theme';
import type { RootStackParamList } from '../types';
import TonyteBackground from '../components/TonyteBackground';
import { auth } from '../firebase';
import { useProfile } from '../hooks/useProfile';
import { computeCompletionScore } from '../utils/profileCompletion';
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES, SupportedLanguage, useI18n } from '../i18n';
import { useTheme } from '../theme/useTheme';
import type { ThemeName, TonyteTheme } from '../theme/themes';
import { daylightStyle } from '../theme/daylightStyle';
import { getInstalledAppVersion } from '../utils/appVersion';
import { nudgeSession } from '../utils/nudgeSession';
import { canOpenStoreReviewPage, openStoreReviewPage } from '../utils/storeReview';
import { loadSurveyConfig } from '../services/optionalNudges';
import { requestNotificationPermission } from '../utils/permissions';
import { isPartner } from '../utils/roles';

type Props = NativeStackScreenProps<RootStackParamList, 'Settings'>;

const SettingsScreen: React.FC<Props> = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const uid = auth.currentUser?.uid ?? null;
  const { profile, updateProfile } = useProfile(uid);
  const { language, setLanguage, t } = useI18n();
  const [surveyActive, setSurveyActive] = useState(false);
  const [versionLabel, setVersionLabel] = useState<string>('');
  const [notificationSaving, setNotificationSaving] = useState(false);
  useEffect(() => {
    let active = true;
    void loadSurveyConfig().then(config => { if (active) setSurveyActive(config?.active === true && Boolean(config.surveyId)); });
    void getInstalledAppVersion().then(info => {
      if (!active) return;
      if (Platform.OS === 'web') setVersionLabel(t('optionalSettings.webVersion'));
      else if (info) setVersionLabel(`${info.versionName} (${info.versionCode})`);
    }).catch(() => undefined);
    return () => { active = false; };
  }, [t]);
  const {
    theme, themeName, setThemeName, canChangeTheme, seasonalTheme,
    seasonalAppearanceEnabled, seasonalAnimationsEnabled,
    setSeasonalAppearanceEnabled, setSeasonalAnimationsEnabled,
  } = useTheme();
  const themedStyles = useMemo(() => createThemeStyles(theme), [theme]);
  const { score: completionScore } = computeCompletionScore(profile);
  const notificationsEnabled = (profile as { notificationsEnabled?: boolean } | null)?.notificationsEnabled !== false;
  const isPartnerAccount = isPartner(profile?.role);

  const logout = () => {
    Alert.alert(t('settings.logout'), t('profile.signOutConfirm'), [
      { text: t('common.cancel'), style: 'cancel' },
      { text: t('settings.logout'), style: 'destructive', onPress: () => { void AsyncStorage.clear().finally(() => signOut(auth)); } },
    ]);
  };

  const deleteAccount = () => {
    Alert.alert(t('settings.deleteAccountTitle'), t('settings.deleteAccountConfirm'), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('settings.deleteAccount'),
        style: 'destructive',
        onPress: async () => {
          try {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error(t('profile.mustSignInToDelete'));
            const idToken = await currentUser.getIdToken(true);
            const response = await fetch('https://europe-west3-triptribe-8babd.cloudfunctions.net/deleteAccountHttpEu', {
              method: 'POST',
              headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ data: null }),
            });
            const body = await response.json().catch(() => null);
            if (!response.ok || body?.error) throw new Error(body?.error?.message || t('profile.deleteUnavailable'));
            await AsyncStorage.clear().catch(() => undefined);
            await signOut(auth).catch(() => undefined);
          } catch (error) {
            Alert.alert(t('common.error'), error instanceof Error ? error.message : t('profile.deleteUnavailable'));
          }
        },
      },
    ]);
  };

  const selectLanguage = (nextLanguage: SupportedLanguage) => {
    setLanguage(nextLanguage)
      .then(() => updateProfile({ preferredLanguage: nextLanguage }))
      .catch(() => Alert.alert(t('common.error'), t('settings.languageSaveError')));
  };

  const setPushNotificationsEnabled = async (enabled: boolean) => {
    if (notificationSaving) return;
    setNotificationSaving(true);
    try {
      if (enabled) {
        const permission = await requestNotificationPermission();
        if (permission === 'denied' || permission === 'blocked') return;
      }
      await updateProfile({ notificationsEnabled: enabled } as Parameters<typeof updateProfile>[0]);
    } catch (error) {
      Alert.alert(
        t('common.error'),
        error instanceof Error ? error.message : t('common.tryAgain'),
      );
    } finally {
      setNotificationSaving(false);
    }
  };

  return (
    <TonyteBackground appearanceAware style={[styles.container, themedStyles.container]}>
      <StatusBar barStyle={theme.statusBarStyle} backgroundColor={theme.colors.background} />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={{ paddingBottom: insets.bottom + Spacing.xl }}
      >
      <View style={[styles.header, themedStyles.header, { paddingTop: insets.top + Spacing.sm }]}> 
        <TouchableOpacity onPress={() => navigation.goBack()} style={[styles.backBtn, themedStyles.backBtn]}>
          <Icon name="arrow-left" size={20} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, themedStyles.textPrimary]}>{t('settings.title')}</Text>
        <View style={{ width: 60 }} />
      </View>

      <Section title={t('settings.account')}>
        {!isPartnerAccount ? <SettingsRow
          icon="edit-2"
          label={t('settings.editProfile')}
          onPress={() => navigation.navigate('EditProfile')}
        /> : null}
        {!isPartnerAccount ? <SettingsRow
          icon="user-check"
          label={t('settings.verificationCenter')}
          onPress={() => navigation.navigate('VerificationCenter')}
        /> : null}
        {isPartnerAccount ? <SettingsRow icon="briefcase" label={t('settings.partnerDashboard')} onPress={() => navigation.navigate('PartnerHome')} /> : null}
      </Section>

      {profile?.role === 'admin' && canChangeTheme ? (
        <Section title={t('settings.appearance')}>
          <View style={styles.languageGrid}>
            {([
              { name: 'midnight' as ThemeName, label: t('settings.midnight'), icon: 'moon' },
              { name: 'daylight' as ThemeName, label: t('settings.daylight'), icon: 'sun' },
            ]).map(option => {
              const selected = themeName === option.name;
              return (
                <TouchableOpacity
                  key={option.name}
                  style={[
                    styles.themeButton,
                    themedStyles.choiceButton,
                    selected && styles.themeButtonActive,
                    selected && themedStyles.choiceButtonActive,
                  ]}
                  onPress={() => { void setThemeName(option.name); }}
                  activeOpacity={0.78}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: selected }}
                >
                  <Icon
                    name={option.icon}
                    size={16}
                    color={selected ? theme.colors.accent : theme.colors.iconMuted}
                  />
                  <Text style={[
                    styles.languageButtonText,
                    themedStyles.textMuted,
                    selected && styles.languageButtonTextActive,
                    selected && themedStyles.choiceTextActive,
                  ]}>
                    {option.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </Section>
      ) : null}

      <Section title="Seasonal appearance">
        <PreferenceToggle
          icon="star"
          label="Seasonal colors and artwork"
          detail={seasonalTheme ? `Active: ${seasonalTheme.name}` : 'Use remotely published Tonyte occasions when available'}
          value={seasonalAppearanceEnabled}
          onChange={enabled => { void setSeasonalAppearanceEnabled(enabled); }}
        />
        <PreferenceToggle
          icon="wind"
          label="Seasonal animations"
          detail="Keep seasonal colors and artwork when animations are off"
          value={seasonalAnimationsEnabled}
          disabled={!seasonalAppearanceEnabled}
          onChange={enabled => { void setSeasonalAnimationsEnabled(enabled); }}
        />
      </Section>

      <Section title={t('settings.language')}>
        <View style={styles.languageGrid}>
          {SUPPORTED_LANGUAGES.map((option) => {
            const selected = language === option;
            return (
              <TouchableOpacity
                key={option}
                style={[
                  styles.languageButton,
                  themedStyles.choiceButton,
                  selected && styles.languageButtonActive,
                  selected && themedStyles.languageButtonActive,
                ]}
                onPress={() => selectLanguage(option)}
                activeOpacity={0.78}
              >
                <Text style={[
                  styles.languageButtonText,
                  themedStyles.textMuted,
                  selected && styles.languageButtonTextActive,
                  selected && themedStyles.languageButtonTextActive,
                ]}>
                  {LANGUAGE_LABELS[option]}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </Section>

      {!isPartnerAccount ? <Section title={t('settings.profileManagement')}>
        {completionScore >= 100 ? (
          <>
            <View style={[styles.completedRow, themedStyles.row]}>
              <View style={[styles.completedIcon, themedStyles.completedIcon]}>
                <Icon name="check" size={14} color="#061014" />
              </View>
              <View style={styles.completedTextBlock}>
                <Text style={[styles.completedTitle, themedStyles.textPrimary]}>{t('settings.profileCompleted')}</Text>
                <Text style={[styles.completedSub, themedStyles.textMuted]}>{t('settings.profileCompletedSub')}</Text>
              </View>
            </View>
            <SettingsRow
              icon="check-square"
              label={t('settings.editProfileAnswers')}
              onPress={() => navigation.navigate('CompleteProfile', { editAll: true })}
            />
          </>
        ) : (
          <SettingsRow
            icon="zap"
            label={`${t('settings.completeProfile')} (${completionScore}%)`}
            onPress={() => navigation.navigate('CompleteProfile')}
          />
        )}
      </Section> : null}

      <Section title={t('settings.notifications')}>
        <PreferenceToggle
          icon="bell"
          label={t('settings.pushNotifications')}
          value={notificationsEnabled}
          disabled={notificationSaving || !uid}
          onChange={setPushNotificationsEnabled}
        />
      </Section>

      <Section title={t('settings.legal')}>
        {isPartnerAccount ? <SettingsRow icon="briefcase" label={t('settings.partnerTerms')} onPress={() => navigation.navigate('PartnerTerms')} /> : null}
        <SettingsRow
          icon="lock"
          label={t('settings.privacyPolicy')}
          onPress={() => navigation.navigate('PrivacyPolicy')}
        />
        <SettingsRow
          icon="file-text"
          label={t('settings.termsOfService')}
          onPress={() => navigation.navigate('TermsOfService')}
        />
        <SettingsRow
          icon="users"
          label={t('settings.communityGuidelines')}
          onPress={() => navigation.navigate('CommunityGuidelines')}
        />
        <SettingsRow
          icon="info"
          label={t('settings.impressum')}
          onPress={() => navigation.navigate('Impressum')}
        />
      </Section>

      {isPartnerAccount ? (
        <Section title={t('settings.support')}>
          <SettingsRow icon="help-circle" label={t('settings.helpFeedback')} onPress={() => { void Linking.openURL('mailto:support@tonyte.app'); }} />
        </Section>
      ) : null}

      {isPartnerAccount ? (
        <Section title={t('settings.session')}>
          <SettingsRow icon="log-out" label={t('settings.logout')} onPress={logout} />
          <SettingsRow icon="trash-2" label={t('settings.deleteAccount')} onPress={deleteAccount} />
        </Section>
      ) : null}

      {__DEV__ ? (
        <Section title="[DEV] Feedback survey">
          <SettingsRow icon="eye" label="Preview feedback survey (nothing is saved)" onPress={() => nudgeSession.requestOpenSurvey({ preview: true })} />
        </Section>
      ) : null}

      {surveyActive || canOpenStoreReviewPage() ? (
        <Section title={t('optionalSettings.feedbackSection')}>
          {surveyActive ? (
            <SettingsRow
              icon="message-square"
              label={t('optionalSettings.giveFeedback')}
              onPress={() => nudgeSession.requestOpenSurvey()}
            />
          ) : null}
          {canOpenStoreReviewPage() ? (
            // A plain store link (never the in-app review API from a button —
            // Apple and Google both advise against that).
            <SettingsRow
              icon="star"
              label={t('optionalSettings.rateTonyte')}
              onPress={() => { void openStoreReviewPage(); }}
            />
          ) : null}
        </Section>
      ) : null}

      <Section title={t('settings.about')}>
        <View style={[styles.aboutCard, themedStyles.sectionCard]}>
          <Text style={[styles.aboutApp, themedStyles.textPrimary]}>Tonyte</Text>
          <Text style={[styles.aboutVersion, themedStyles.textMuted]}>{versionLabel ? t('settings.version').replace('{version}', versionLabel) : null}</Text>
          <Text style={[styles.aboutTagline, themedStyles.textSecondary]}>{t('settings.tagline')}</Text>
        </View>
      </Section>
      </ScrollView>
    </TonyteBackground>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => {
  const { theme } = useTheme();
  const themedStyles = useMemo(() => createThemeStyles(theme), [theme]);
  return (
    <View style={styles.section}>
      <Text style={[styles.sectionTitle, themedStyles.textMuted]}>{title}</Text>
      <View style={[styles.sectionCard, themedStyles.sectionCard]}>{children}</View>
    </View>
  );
};

const SettingsRow: React.FC<{
  icon: string;
  label: string;
  onPress: () => void;
  destructive?: boolean;
}> = ({ icon, label, onPress, destructive }) => {
  const { theme } = useTheme();
  const themedStyles = useMemo(() => createThemeStyles(theme), [theme]);
  return (
    <TouchableOpacity style={[styles.row, themedStyles.row]} onPress={onPress} activeOpacity={0.7}>
      <Icon name={icon} size={16} color={destructive ? theme.colors.error : theme.colors.iconMuted} />
      <Text style={[styles.rowLabel, themedStyles.textPrimary, destructive && { color: theme.colors.error }]}>{label}</Text>
      <Icon name="chevron-right" size={14} color={theme.colors.iconMuted} />
    </TouchableOpacity>
  );
};

const PreferenceToggle: React.FC<{
  icon: string;
  label: string;
  detail?: string;
  value: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}> = ({ icon, label, detail, value, disabled, onChange }) => {
  const { theme } = useTheme();
  const themedStyles = useMemo(() => createThemeStyles(theme), [theme]);
  return (
    <View style={[styles.row, themedStyles.row, disabled && styles.disabledRow]}>
      <Icon name={icon} size={16} color={theme.colors.iconMuted} />
      <View style={styles.preferenceText}>
        <Text style={[styles.rowLabel, themedStyles.textPrimary]}>{label}</Text>
        {detail ? <Text style={[styles.preferenceDetail, themedStyles.textMuted]}>{detail}</Text> : null}
      </View>
      <Switch
        value={value}
        disabled={disabled}
        onValueChange={onChange}
        trackColor={{ false: theme.colors.controlBackground, true: theme.colors.accentSecondary }}
        thumbColor={value ? theme.colors.surfaceElevated : theme.colors.iconMuted}
        accessibilityLabel={label}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.bg },
  scroll: { flex: 1, backgroundColor: 'transparent' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.base,
    paddingBottom: Spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'transparent',
  },
  headerTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.md,
    fontWeight: Typography.bold,
  },
  section: {
    paddingHorizontal: Spacing.base,
    marginTop: Spacing.xl,
  },
  sectionTitle: {
    color: Colors.textMuted,
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: Spacing.sm,
  },
  sectionCard: {
    backgroundColor: 'rgba(4,8,15,0.72)',
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Colors.border,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.base,
    paddingVertical: Spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
    gap: Spacing.md,
  },
  rowLabel: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.base,
  },
  preferenceText: { flex: 1, minWidth: 0 },
  preferenceDetail: {
    marginTop: 3,
    color: Colors.textMuted,
    fontSize: Typography.xs,
    lineHeight: 17,
  },
  disabledRow: { opacity: 0.5 },
  aboutCard: {
    backgroundColor: 'rgba(4,8,15,0.72)',
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Colors.border,
    padding: Spacing.xl,
    alignItems: 'center',
  },
  completedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
    paddingHorizontal: Spacing.base,
    paddingVertical: Spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  completedIcon: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.turquoise,
  },
  completedTextBlock: { flex: 1, minWidth: 0 },
  completedTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },
  completedSub: {
    marginTop: 2,
    color: Colors.textMuted,
    fontSize: Typography.xs,
  },
  languageGrid: {
    padding: Spacing.base,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.sm,
  },
  languageButton: {
    minHeight: 38,
    borderRadius: Radius.pill,
    paddingHorizontal: Spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: Colors.border,
  },
  languageButtonActive: {
    backgroundColor: 'rgba(38,208,206,0.16)',
    borderColor: 'rgba(38,208,206,0.42)',
  },
  languageButtonText: {
    color: Colors.textMuted,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  languageButtonTextActive: {
    color: Colors.turquoise,
  },
  themeButton: {
    minWidth: 126,
    minHeight: 44,
    borderRadius: Radius.lg,
    paddingHorizontal: Spacing.base,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.sm,
    borderWidth: 1,
  },
  themeButtonActive: {
    shadowColor: Colors.sunset,
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.16,
    shadowRadius: 9,
    elevation: 3,
  },
  aboutApp: {
    color: Colors.textPrimary,
    fontSize: Typography.xl,
    fontWeight: Typography.extrabold,
    marginBottom: Spacing.xs,
  },
  aboutVersion: { color: Colors.textMuted, fontSize: Typography.sm },
  aboutTagline: {
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    marginTop: Spacing.xs,
  },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  header: daylightStyle(theme, { borderBottomColor: theme.colors.divider }),
  backBtn: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.border,
    ...theme.shadows.card,
  }),
  sectionCard: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.border,
    ...theme.shadows.card,
  }),
  row: daylightStyle(theme, { borderBottomColor: theme.colors.divider }),
  completedIcon: daylightStyle(theme, { backgroundColor: theme.colors.accentSecondary }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  choiceButton: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.border,
  }),
  choiceButtonActive: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackgroundActive,
    borderColor: theme.colors.accent,
  }),
  choiceTextActive: daylightStyle(theme, { color: theme.colors.accent }),
  languageButtonActive: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackgroundActive,
    borderColor: theme.colors.accentSecondary,
  }),
  languageButtonTextActive: daylightStyle(theme, { color: theme.colors.accentSecondary }),
});

export default SettingsScreen;
